//
//  screen_on_component.js
//  Cell-Ed
//
//  Copyright © 2020 Cell-Ed. All rights reserved.
//
import { Component } from 'react';
import { connect } from 'react-redux';

import { Plugins, Capacitor } from '@capacitor/core';

/**
 * The native keep-awake API, under whichever name this app build registers it.
 *
 * Current Android and iOS builds register @capacitor-community/keep-awake as `KeepAwake`. iOS 3.8.x
 * registered `CapacitorKeepScreenOn`, and learners who have not updated still run it. Before this,
 * only the old name was checked, so Android never kept the screen on at all.
 */
function keepAwakeApi() {
  if (Capacitor.isPluginAvailable('KeepAwake')) {
    return { on: () => Plugins.KeepAwake.keepAwake(), off: () => Plugins.KeepAwake.allowSleep() };
  }
  if (Capacitor.isPluginAvailable('CapacitorKeepScreenOn')) {
    return { on: () => Plugins.CapacitorKeepScreenOn.enable(), off: () => Plugins.CapacitorKeepScreenOn.disable() };
  }
  return null;
}

/**
 * Keeps the screen awake while it matters: during lesson audio, and during an active walk -- the
 * learner is carrying the phone and watching the timer, and a six-minute walk test must not end in a
 * locked screen. Previously only audio counted.
 */
class ScreenOnComponent extends Component {
  constructor(props) {
    super(props);
    this.keepScreenOn = null;
  }

  componentDidMount() {
    this.sync();
  }

  componentDidUpdate() {
    this.sync();
  }

  componentWillUnmount() {
    this.setScreenOn(false);
  }

  sync() {
    this.setScreenOn(this.props.isAudioPlaying || this.props.isWalkActive);
  }

  setScreenOn(value) {
    if (value === this.keepScreenOn) return;
    const api = keepAwakeApi();
    if (!api) return;

    this.keepScreenOn = value;
    (value ? api.on() : api.off()).catch((error) => {
      console.log(`[ScreenOn] unable to ${value ? 'keep the screen on' : 'allow sleep'}: ${error && error.message}`);
      this.keepScreenOn = null; // try again on the next update
    });
  }

  render() {
    return null;
  }
}

function mapStateToProps(state) {
  return {
    isAudioPlaying: Boolean(state.audio && state.audio.isPlaying),
    // Set by PLATFORM_CLIENT_EXERCISE_TIMER_START_EVENT and cleared by _END_EVENT, which the server's
    // ActivityWalkMeasureControllerStep sends around an open walk or a six-minute walk test.
    isWalkActive: Boolean(state.questionnaires && state.questionnaires.activeTimerStartDate)
  };
}

export default connect(mapStateToProps, null)(ScreenOnComponent);
